import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Icon } from '@/lib/icons';
import { Button } from '@/components/ui/Button';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  reset = (): void => this.setState({ error: null });

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;

    const isNetwork =
      this.state.error.message.toLowerCase().includes('network') ||
      this.state.error.message.toLowerCase().includes('failed to fetch');

    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-50 px-6">
        <div className="max-w-lg rounded-lg border border-ink-200 bg-paper p-8 shadow-elev-2">
          <Icon name="AlertOctagon" size={28} className="text-danger" />
          <h1 className="mt-3 text-xl font-semibold text-ink-900">
            {isNetwork ? 'API nicht erreichbar' : 'Unerwarteter Fehler'}
          </h1>
          {isNetwork ? (
            <div className="mt-2 space-y-2 text-sm text-ink-600">
              <p>
                Das Ignis-AI-Backend antwortet gerade nicht. Bitte versuchen Sie es in einem Moment
                erneut.
              </p>
              {import.meta.env.DEV ? (
                <pre className="rounded bg-ink-900 px-3 py-2 font-mono text-xs text-paper">
                  pnpm --filter api dev
                </pre>
              ) : null}
            </div>
          ) : (
            <div className="mt-2 space-y-2 text-sm text-ink-600">
              {/* Keine rohe error.message im Produktions-Build — die steht im
                  Console-Log; dem Nutzer hilft nur ein Neuladen. */}
              <p>
                Ein unerwarteter Fehler ist aufgetreten. Bitte laden Sie die Seite neu — falls das
                Problem bestehen bleibt, wenden Sie sich an den Support.
              </p>
              {import.meta.env.DEV ? (
                <pre className="overflow-auto rounded bg-ink-900 px-3 py-2 font-mono text-xs text-paper">
                  {this.state.error.message}
                </pre>
              ) : null}
            </div>
          )}
          <div className="mt-5 flex gap-2">
            <Button onClick={this.reset} variant="primary">
              Erneut versuchen
            </Button>
            <Button onClick={() => window.location.reload()} variant="secondary">
              Seite neu laden
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
